import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { cn } from '@/lib/cn'

import type { Alert, Alerts, AlertSeverity } from '../types'

/**
 * Ce qui demande une décision, en tête du tableau de bord.
 *
 * Deux choix portent tout le reste :
 *
 * * **Rien à signaler est un état affiché.** Un bloc qui montre toujours
 *   quelque chose finit par ne plus être lu, et le jour où il compte vraiment,
 *   personne ne le regarde. Le silence doit donc être visible comme tel.
 * * **Aucune alerte ne se ferme à la main.** Elle disparaît quand le fait
 *   disparaît. Pouvoir la faire taire sans régler le problème reviendrait à
 *   pouvoir la faire taire tout court.
 *
 * Les libellés viennent du serveur, mais la traduction est tentée d'abord sur
 * le `code` — stable et technique. Une alerte ajoutée côté serveur sans
 * traduction s'affiche donc dans sa version française plutôt que de ne rien
 * afficher du tout.
 */
const TONES: Record<AlertSeverity, string> = {
  critical: 'border-l-ember-400 text-ember-300',
  warning: 'border-l-champagne-400 text-champagne-300',
  info: 'border-l-line-strong text-content-secondary',
}

export function AlertsCard({ alerts }: { alerts: Alerts | undefined }) {
  const { t } = useTranslation()
  if (!alerts) return null

  return (
    <section className="rounded-lg border border-line-subtle bg-bg-elevated p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="font-display text-heading-md text-content-primary">
          {t('admin.alerts.title')}
        </h2>
        {alerts.critical > 0 ? (
          <span className="font-mono text-caption uppercase text-ember-300">
            {t('admin.alerts.criticalCount', { count: alerts.critical })}
          </span>
        ) : null}
      </div>

      {alerts.alerts.length === 0 ? (
        <p className="mt-4 text-sm text-content-muted">{t('admin.alerts.allClear')}</p>
      ) : (
        <ul className="mt-4 space-y-2">
          {alerts.alerts.map((alert) => (
            <AlertRow key={alert.code} alert={alert} />
          ))}
        </ul>
      )}
    </section>
  )
}

function AlertRow({ alert }: { alert: Alert }) {
  const { t } = useTranslation()

  const title = t(`admin.alerts.code.${alert.code}.title`, {
    defaultValue: alert.title,
    count: alert.count,
  })
  const detail = t(`admin.alerts.code.${alert.code}.detail`, {
    defaultValue: alert.detail,
    count: alert.count,
  })

  const body = (
    <>
      <p className="flex items-baseline gap-2">
        <span className="text-sm text-content-primary">{title}</span>
        {alert.count > 1 ? (
          <span className="font-mono text-caption tabular-nums text-content-muted">
            ×{alert.count}
          </span>
        ) : null}
      </p>
      <p className="mt-0.5 text-xs text-content-muted">{detail}</p>
    </>
  )

  const className = cn('block border-l-2 py-1.5 pl-3', TONES[alert.severity])

  // Une alerte sans écran dédié reste affichée : ne pas pouvoir cliquer ne la
  // rend pas moins vraie.
  return (
    <li>
      {alert.href ? (
        <Link to={alert.href} className={cn(className, 'hover:bg-bg-overlay/60')}>
          {body}
        </Link>
      ) : (
        <div className={className}>{body}</div>
      )}
    </li>
  )
}
